import { ApiAgentExecuteFileOutput, ApiAgentExecuteTextOutput } from 'sprites-types/api/agents'

// Consolidated file type utilities
const FileTypeUtils = {
  // File extension to type mapping
  extensionToType: {
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',
    mp3: 'audio',
    wav: 'audio',
    ogg: 'audio',
    m4a: 'audio',
    mp4: 'video',
    webm: 'video',
    avi: 'video',
    mov: 'video',
    pdf: 'pdf',
    csv: 'csv',
    txt: 'text',
    md: 'markdown',
    html: 'html',
    htm: 'html',
  } as Record<string, string>,

  // Type to default extension mapping
  typeToExtension: {
    image: 'png',
    audio: 'mp3',
    video: 'mp4',
    pdf: 'pdf',
    csv: 'csv',
    text: 'txt',
    markdown: 'md',
    html: 'html',
    file: 'bin',
  } as Record<string, string>,

  // MIME type to extension mapping
  mimeToExtension: {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'application/pdf': 'pdf',
    'text/csv': 'csv',
    'text/plain': 'txt',
    'text/html': 'html',
  } as Record<string, string>,

  detectTypeFromUrl(url: string): string | null {
    const extension = url.split('.').pop()?.toLowerCase()
    return extension ? this.extensionToType[extension] || null : null
  },

  getExtensionForType(fileType: string): string {
    return this.typeToExtension[fileType] || 'bin'
  },

  typeFromMime(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('audio/')) return 'audio'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType === 'application/pdf') return 'pdf'
    if (mimeType === 'text/csv') return 'csv'
    if (mimeType === 'text/html') return 'html'
    if (mimeType.startsWith('text/')) return 'text'
    return 'file'
  },

  extensionFromMime(mimeType: string): string {
    return this.mimeToExtension[mimeType] || 'bin'
  },
}

// Utility function to convert array to CSV
const arrayToCsv = (data: any[]): string => {
  if (data.length === 0) return ''

  const headers = Object.keys(data[0])
  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((header) => {
          const cell = row[header]
          const escaped = String(cell).replace(/"/g, '""')
          return `"${escaped}"`
        })
        .join(',')
    ),
  ]

  return csvRows.join('\n')
}

// Recursive helper function to extract outputs from any value at any depth
const extractOutputsFromValue = (
  value: any,
  keyPath: string,
  outputs: (ApiAgentExecuteTextOutput | ApiAgentExecuteFileOutput)[]
): void => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return
  }

  // Handle primitives (strings, numbers, booleans)
  if (typeof value !== 'object') {
    // Handle strings - check if it's a file URL first, otherwise treat as text
    if (typeof value === 'string') {
      const fileType = FileTypeUtils.detectTypeFromUrl(value)
      if (fileType && (value.startsWith('http://') || value.startsWith('https://'))) {
        outputs.push({
          type: 'file',
          filename: `${keyPath}.${FileTypeUtils.getExtensionForType(fileType)}`,
          fileType,
          source: { kind: 'url', value },
          userFacing: true,
        } as ApiAgentExecuteFileOutput)
      } else {
        // Convert string to text output
        outputs.push({
          type: 'text',
          content: value,
          userFacing: true,
        } as ApiAgentExecuteTextOutput)
      }
      return
    }

    // Handle numbers - convert to text output with nice formatting
    if (typeof value === 'number') {
      outputs.push({
        type: 'text',
        content: value.toLocaleString(),
        userFacing: true,
      } as ApiAgentExecuteTextOutput)
      return
    }

    // Handle booleans - convert to text output
    if (typeof value === 'boolean') {
      outputs.push({
        type: 'text',
        content: value ? 'true' : 'false',
        userFacing: true,
      } as ApiAgentExecuteTextOutput)
      return
    }

    // Handle other primitives - convert to text
    outputs.push({
      type: 'text',
      content: String(value),
      userFacing: true,
    } as ApiAgentExecuteTextOutput)
    return
  }

  // Handle objects (including arrays)

  // Check if it's already a properly formatted file output
  if ('filename' in value && 'fileType' in value && 'source' in value) {
    outputs.push(value as ApiAgentExecuteFileOutput)
    return
  }

  // Check if it's an object with a URL
  if ('url' in value && typeof value.url === 'string') {
    const url: string = value.url
    const fileType: string | null = FileTypeUtils.detectTypeFromUrl(url)
    if (fileType) {
      outputs.push({
        type: 'file',
        filename: `${keyPath}.${FileTypeUtils.getExtensionForType(fileType)}`,
        fileType,
        source: { kind: 'url', value: url },
        userFacing: true,
      } as ApiAgentExecuteFileOutput)
    } else {
      outputs.push({
        type: 'text',
        content: JSON.stringify(value, null, 2),
        userFacing: true,
      } as ApiAgentExecuteTextOutput)
    }
    return
  }

  // Check if it's base64 encoded file data
  if (
    'data' in value &&
    typeof value.data === 'string' &&
    'mimeType' in value &&
    typeof value.mimeType === 'string'
  ) {
    const fileType: string = FileTypeUtils.typeFromMime(value.mimeType)
    outputs.push({
      type: 'file',
      filename: `${keyPath}.${FileTypeUtils.extensionFromMime(value.mimeType)}`,
      fileType,
      source: { kind: 'raw', value: value.data },
      userFacing: true,
    } as ApiAgentExecuteFileOutput)
    return
  }

  // Check if it's an array of strings - join them with commas instead of exploding them into multiple text outputs
  if (Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'string')) {
    outputs.push({
      type: 'text',
      content: value.join(', '),
      userFacing: true,
    } as ApiAgentExecuteTextOutput)
    return
  }

  // Check if it's CSV data (array of objects)
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null
  ) {
    const csvContent: string = arrayToCsv(value)
    outputs.push({
      type: 'file',
      filename: `${keyPath}_data.csv`,
      fileType: 'csv',
      source: { kind: 'raw', value: csvContent },
      userFacing: true,
    } as ApiAgentExecuteFileOutput)
    return
  }

  // For other objects/arrays, recursively process each property/element
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      extractOutputsFromValue(item, `${keyPath}[${index}]`, outputs)
    })
  } else {
    // It's a regular object - recursively process each property
    Object.entries(value).forEach(([key, nestedValue]) => {
      const nestedKeyPath: string = keyPath ? `${keyPath}.${key}` : key
      extractOutputsFromValue(nestedValue, nestedKeyPath, outputs)
    })
  }
}

// Consolidated result processing function
export const processNodeResultsToOutputs = (
  nodeResults: any[]
): (ApiAgentExecuteTextOutput | ApiAgentExecuteFileOutput)[] => {
  const outputs: (ApiAgentExecuteTextOutput | ApiAgentExecuteFileOutput)[] = []

  nodeResults.forEach((nodeResult: any) => {
    if (!nodeResult.outputs) return
    // Handle case where nodeResult.outputs is a string (not an object)
    if (typeof nodeResult.outputs === 'string') {
      outputs.push({
        type: 'text',
        content: nodeResult.outputs,
        userFacing: true,
      } as ApiAgentExecuteTextOutput)
      return
    }

    // Recursively extract outputs from the nodeResult.outputs, using nodeId as the base key path
    const baseKeyPath: string = nodeResult.nodeId || 'output'
    extractOutputsFromValue(nodeResult.outputs, baseKeyPath, outputs)
  })

  return outputs
}

// Consolidated unified workflow result processing
export interface ProcessedWorkflowResult {
  success: boolean
  outputs: (ApiAgentExecuteTextOutput | ApiAgentExecuteFileOutput)[]
  nodeResults?: any[]
  workflowExecutionId?: string
  summary?: string
  message?: string
}

// Unified function to process any workflow result format into consistent outputs
export const processWorkflowResult = (result: any): ProcessedWorkflowResult => {
  // Handle case where result is just a string
  if (typeof result === 'string') {
    return {
      success: true,
      outputs: [
        {
          type: 'text',
          content: result,
          userFacing: true,
        } as ApiAgentExecuteTextOutput,
      ],
      nodeResults: [],
      summary: result,
      message: result,
    }
  }

  // Handle case where result is an array (nodeResults directly)
  if (Array.isArray(result)) {
    const outputs = processNodeResultsToOutputs(result)
    return {
      success: true,
      outputs,
      nodeResults: result,
      summary: `Workflow completed with ${result.length} node result(s)`,
      message: 'Workflow executed successfully',
    }
  }

  // Handle object result
  if (result && typeof result === 'object') {
    let outputs: (ApiAgentExecuteTextOutput | ApiAgentExecuteFileOutput)[] = []

    // Use existing outputs if available
    if (result.outputs && Array.isArray(result.outputs)) {
      outputs = result.outputs
    } else if (typeof result === 'object' && result.nodeResults) {
      outputs = processNodeResultsToOutputs(result.nodeResults)
    }
    // Otherwise, process nodeResults if available
    else if (result.nodeResults && Array.isArray(result.nodeResults)) {
      outputs = processNodeResultsToOutputs(result.nodeResults)
    }

    // If still no outputs, create a fallback summary output
    if (outputs.length === 0) {
      outputs = [
        {
          type: 'text',
          content: result.summary || result.message || 'Workflow completed successfully!',
          userFacing: true,
        } as ApiAgentExecuteTextOutput,
      ]
    }

    return {
      success: result.success !== false,
      outputs,
      nodeResults: result.nodeResults,
      summary: result.summary,
      message: result.message,
    }
  }

  // Fallback for any other format
  return {
    success: true,
    outputs: [
      {
        type: 'text',
        content: typeof result === 'string' ? result : JSON.stringify(result, null, 2),
        userFacing: true,
      } as ApiAgentExecuteTextOutput,
    ],
  }
}
